import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SHSForm from '../../utils/shs-form'
import InputElement from '../../utils/input'
import StateGraph from './state-graph'
import { engineStateLabel } from '../../../constants/engine-states'

const statusBadge = (statusValue, unlocked) => {
    if (!unlocked) {
        return <span className="rt-badge locked"><FontAwesomeIcon icon="lock" /> Locked</span>
    }
    switch (statusValue) {
    case 'PASSED':
        return <span className="rt-badge passed"><FontAwesomeIcon icon="circle-check" /> Passed</span>
    case 'FAILED':
        return <span className="rt-badge failed"><FontAwesomeIcon icon="circle-xmark" /> Failed</span>
    case 'SKIPPED':
        return <span className="rt-badge skipped">Skipped</span>
    case 'IN_PROGRESS':
        return <span className="rt-badge active">In progress</span>
    default:
        return <span className="rt-badge available">Not started</span>
    }
}

// Human-readable summary of a signal step's pass criteria (shown alongside the read/monitor UI).
const signalCriteria = (step) => {
    const v = step.verification || {}
    const ui = step.ui || {}
    switch (v.kind) {
    case 'signal_static':
        return <p className="rt-criteria">Signal <code>{v.signal}</code> {v.operator} {v.threshold ?? v.expected}{v.unit || ''}</p>
    case 'duration_in_state':
        return <p className="rt-criteria">{ui.monitorMetricLabel || 'Time in state'}: needs ≥ {Math.round((v.minDurationSeconds || 0) / 60)} min in {v.stateLabel || `state ${v.state}`}</p>
    case 'state_cycles':
        return <p className="rt-criteria">{ui.monitorMetricLabel || 'Cycles'}: needs ≥ {v.minCycles} × {v.sequenceLabel || (v.sequence || []).join(' → ')}</p>
    case 'state_transition':
        return <p className="rt-criteria">Must progress: {v.sequenceLabel || (v.sequence || []).join(' → ')}</p>
    default:
        return null
    }
}

const RoadTestStep = ({ step, result, media, unlocked, expanded, onToggle, submitStepUpdate, submitStepPhotos, advanceStep, readSignal, onComplete, startMonitoring, stopMonitoring, resetMonitoring, monitoring, imei }) => {
    const [previewUrl, setPreviewUrl] = useState(null)
    const statusValue = result?._computed?.statusValue
    const kind = step.verification?.kind
    const ui = step.ui || {}

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl)
            }
        }
    }, [previewUrl])

    const handleFileChange = (id, file) => {
        if (file) {
            setPreviewUrl(URL.createObjectURL(file))
        }
    }

    // A form submit completes the step server-side; refetch + (conditionally) advance is handled by
    // the panel, which only moves on once the step actually resolves to PASSED.
    const handleFormComplete = () => onComplete(step.key)

    // Live progress readout for a window step, from the server-side evaluator metrics.
    const renderMonitorMetrics = () => {
        const m = result?.result?.metrics
        if (!m) {
            return null
        }
        if (kind === 'duration_in_state') {
            const current = Math.round((m.maxContiguousSeconds || 0) / 60)
            const required = Math.round((m.requiredSeconds || 0) / 60)
            return <p className="rt-metric">{current} / {required} min in {engineStateLabel(m.targetState)} · now: {engineStateLabel(m.currentState)}</p>
        }
        if (kind === 'state_cycles') {
            return <p className="rt-metric">{m.cycles} / {m.requiredCycles} cycles completed</p>
        }
        if (kind === 'state_transition') {
            return <p className="rt-metric">{m.observed ? 'Sequence observed ✓' : 'Waiting for sequence…'}</p>
        }
        return null
    }

    const renderPhotos = () => {
        if (!previewUrl && media.length === 0) {
            return null
        }
        return (
            <div className="rt-photos">
                {previewUrl &&
                    <div className="image-wrapper">
                        <img src={previewUrl} alt="preview" />
                    </div>
                }
                {media.map(m => (
                    <div key={m.id} className="image-wrapper">
                        <img src={`${process.env.REACT_APP_API_URL}/road-tests/step-media/${m.id}/image/?iv=${m.imageId}`} alt="uploaded" />
                    </div>
                ))}
            </div>
        )
    }

    const renderBody = () => {
        if (kind === 'photo') {
            return (
                <div className="rt-step-body">
                    <p className="rt-instructions">{step.description}</p>
                    <SHSForm useFormData={true} resetForm={true} submitFX={(formData, formId) => submitStepPhotos(step.key, formData, formId)} successFX={handleFormComplete} buttonLabel={ui.photoLabel ? `Upload ${ui.photoLabel}` : 'Upload photo'}>
                        <InputElement type="file" accept="image/*" id="photo" label={ui.photoLabel || 'Photo'} required onChange={handleFileChange} />
                        {ui.allowNotes && <InputElement type="textarea" id="notes" label="Notes (optional)" />}
                    </SHSForm>
                    {renderPhotos()}
                </div>
            )
        }

        if (kind === 'manual_visual') {
            return (
                <div className="rt-step-body">
                    <p className="rt-instructions">{step.description}</p>
                    <SHSForm resetForm={true} submitFX={(formData, formId) => submitStepUpdate(step.key, { status: 'PASSED', notes: formData.notes }, formId)} successFX={handleFormComplete} buttonLabel={ui.confirmLabel || 'Mark complete'}>
                        <InputElement type="textarea" id="notes" label="Notes (optional)" />
                    </SHSForm>
                </div>
            )
        }

        // Static signal steps (fuel level, faults): read + evaluate a point-in-time value.
        if (kind === 'signal_static' || kind === 'all_faults_clear') {
            const read = result?.result?.read
            const readPassed = result?.result?.passed
            return (
                <div className="rt-step-body">
                    <p className="rt-instructions">{step.description}</p>
                    <div className="rt-signal">
                        {signalCriteria(step)}
                        {read &&
                            <div className={`rt-read ${readPassed ? 'pass' : 'fail'}`}>
                                {kind === 'signal_static'
                                    ? <p><code>{read.signal}</code> = {String(read.value)}{read.unit || ''} {readPassed ? '✓' : '✗'} <span className="rt-target">(needs {read.operator} {read.target})</span></p>
                                    : <p>{readPassed ? 'All faults clear ✓' : `Faults present: ${Object.keys(read.failing || {}).join(', ')}`}</p>}
                            </div>}
                    </div>
                    <div className="rt-actions">
                        <button type="button" className="btn small" disabled={!imei} onClick={() => readSignal(step.key)}>{ui.readLabel || 'Read signal'}</button>
                        <button type="button" className="btn small light" onClick={() => advanceStep(step.key, 'PASSED')}>Mark passed</button>
                        <button type="button" className="btn small red" onClick={() => advanceStep(step.key, 'FAILED')}>Mark failed</button>
                    </div>
                </div>
            )
        }

        // State/window steps: live-monitor the engine state over the target window with a graph. The
        // graph sizing and test start time come back from the server with the series. The start is
        // also printed below the graph, since its marker scrolls off once the window fills.
        const series = result?.result?.series || []
        const windowStartedAt = result?.result?.windowStartedAt || null
        // Before the first poll there's no server sizing yet, so mirror RoadTestDefinition's
        // fallbacks (window: windowSeconds → minDurationSeconds → 1800s; buffer: bufferSeconds →
        // 300s) and the graph doesn't jump when the first response lands.
        const windowSeconds = result?.result?.windowSeconds || step.verification?.windowSeconds || step.verification?.minDurationSeconds || 1800
        const bufferSeconds = result?.result?.bufferSeconds ?? step.verification?.bufferSeconds ?? 300
        // Reset is the only way to re-open a step: allow it once a run has started, or once the step
        // has been closed (even by hand before monitoring ever ran). A closed step can't be monitored
        // until it's reset — the server refuses those polls, and its run's data is final.
        const closed = ['PASSED', 'FAILED', 'SKIPPED'].includes(statusValue)
        const canReset = !!windowStartedAt || closed
        const canMonitor = !!imei && !closed
        return (
            <div className="rt-step-body">
                <p className="rt-instructions">{step.description}</p>
                <div className="rt-signal">
                    {signalCriteria(step)}
                    <StateGraph series={series} windowSeconds={windowSeconds} bufferSeconds={bufferSeconds} windowStartedAt={windowStartedAt} />
                    <p className="rt-collection">
                        {windowStartedAt
                            ? `Test started ${new Date(windowStartedAt * 1000).toLocaleString()} · showing the most recent ${Math.round((windowSeconds + bufferSeconds) / 60)} min`
                            : 'Test not started yet.'}
                    </p>
                    {renderMonitorMetrics()}
                </div>
                <div className="rt-actions">
                    {monitoring
                        ? <button type="button" className="btn small" onClick={() => stopMonitoring()}>Stop monitoring</button>
                        : <button type="button" className="btn small" disabled={!canMonitor} onClick={() => startMonitoring(step.key)}>Start monitoring</button>}
                    <button type="button" className="btn small light" disabled={!canReset} onClick={() => resetMonitoring(step.key)}>Reset test</button>
                    <button type="button" className="btn small light" onClick={() => advanceStep(step.key, 'PASSED')}>Mark passed</button>
                    <button type="button" className="btn small red" onClick={() => advanceStep(step.key, 'FAILED')}>Mark failed</button>
                    {ui.skippable && <button type="button" className="btn small light" onClick={() => advanceStep(step.key, 'SKIPPED')}>Skip step</button>}
                </div>
            </div>
        )
    }

    return (
        <div className={`rt-step ${unlocked ? '' : 'locked'} ${expanded ? 'expanded' : ''}`}>
            <button type="button" className="rt-step-header" onClick={() => unlocked && onToggle()} disabled={!unlocked}>
                <span className="rt-step-order">{step.order}</span>
                <span className="rt-step-title">{step.title}</span>
                <span className="rt-step-badge">{statusBadge(statusValue, unlocked)}</span>
                {unlocked && <FontAwesomeIcon className="rt-chevron" icon={expanded ? 'chevron-up' : 'chevron-down'} />}
            </button>
            {expanded && unlocked && renderBody()}
        </div>
    )
}

export default RoadTestStep
